import type {
  EngagementOverview,
  GrowthSeries,
  PlatformOverview,
  SchoolSummary,
} from '../../../shared/api/platform'

/**
 * *"How many of each kind of person is on this platform, and how many are
 * actually using it?"* — computed once, read by four operator screens.
 *
 * 🔴 IT EXISTS BECAUSE FOUR SCREENS ASKED THE SAME QUESTION AND ALL FOUR
 * ANSWERED IT WITH AN EM DASH. The Dashboard, Institutions, Children and Parents
 * screens each carry a row of six stat tiles — "Total Users", "Teachers",
 * "Students", "Parents", "Active Users (7D)" — and every one of them was a
 * hardcoded `—` under a hardcoded "Not measured yet", on a platform whose API
 * has returned all five figures since the tier was built. The predecessor's
 * failure that `platform.ts` warns about in capitals — *"the predecessor
 * rendered 8 hardcoded children while working endpoints sat uncalled"* — had
 * simply reappeared as a dash instead of a fiction.
 *
 * ⚠️ AND ONE OF THEM WAS NOT A DASH. "Active Users by Role" printed *Teachers
 * 892 · Parents 328 · School Admins 87* on a platform with five teachers and
 * four parents, beside a "Students —". A dash is an honest absence; those three
 * were numbers an operator could act on and nothing produced them.
 *
 * 🔴 ONE FOLD, NOT FOUR — `architecture.md` §12, the same rule
 * `GameStatsService` enforces on the server. Four screens deriving "total users"
 * from whatever they happened to have fetched is four chances to publish a
 * different total for the same platform, and the disagreement surfaces as an
 * operator reading two numbers on two tabs and trusting neither.
 *
 * ⚠️ EVERY FIELD IS `number | null`, AND `null` IS NOT ZERO. A figure whose
 * source has not arrived (or whose endpoint this deployment does not serve) is
 * unknown; zero is a measurement. The tiles render `null` as the dash they used
 * to hardcode — the difference is that now the dash means something.
 */
export interface PlatformCensus {
  /** Children with a seat. `PlatformOverview.enrolment.seats`. */
  students: number | null
  /** Active staff with `role: 'TEACHER'`. */
  teachers: number | null
  /** Parents the platform has ever reached, from the engagement bands. */
  parents: number | null
  /**
   * Active staff with `role: 'ADMIN'`, summed over the school list.
   *
   * ⚠️ SUMMED, BECAUSE NO ENDPOINT REPORTS IT AS A TOTAL. `SchoolSummary`
   * carries `adminCount` per school precisely so F2 can show the schools with
   * nobody who can sign in; the platform total is that column added up, and it
   * is exact rather than estimated because the list is every school.
   */
  schoolAdmins: number | null
  /**
   * Everybody, of every kind.
   *
   * ⚠️ `null` UNLESS EVERY PART IS KNOWN. A "total users" built from three of
   * four sources is a smaller number stated with the confidence of a complete
   * one — worse than the dash it replaced.
   */
  totalUsers: number | null
  /** Children who played inside the overview's own window. */
  activeStudents: number | null
  /** The window `activeStudents` covers, so no screen can print it bare. */
  activeWindowDays: number | null
  /** Parents in the engagement window's `active` band. */
  activeParents: number | null
  /** Teachers in the engagement window's `active` band. */
  activeTeachers: number | null
  /** The parent/teacher band window, in days. */
  engagementWindowDays: number | null
  /**
   * 🔴 TRUE WHEN THE ENGAGEMENT READ HIT ITS CAP. Every parent and teacher
   * figure above is then a sample, and `EngagementOverview` says so in writing:
   * *"a screen that renders '62% of parents are active' without saying so is
   * stating something it does not know."* It travels with the numbers so a tile
   * cannot be drawn without it.
   */
  sampled: boolean
}

const EMPTY: PlatformCensus = {
  students: null,
  teachers: null,
  parents: null,
  schoolAdmins: null,
  totalUsers: null,
  activeStudents: null,
  activeWindowDays: null,
  activeParents: null,
  activeTeachers: null,
  engagementWindowDays: null,
  sampled: false,
}

/**
 * Fold whatever has arrived into one census.
 *
 * ⚠️ EVERY ARGUMENT IS NULLABLE BECAUSE THE SCREENS LOAD THEM SEPARATELY. A
 * screen that fetches only the overview gets students, teachers and actives and
 * `null` for the rest, which is exactly right — it renders three real tiles and
 * three honest dashes rather than waiting on a request it never needed.
 */
export function foldCensus(
  overview: PlatformOverview | null,
  engagement: EngagementOverview | null,
  schools: readonly SchoolSummary[] | null,
  growth: GrowthSeries | null = null,
): PlatformCensus {
  const students =
    overview?.enrolment.seats ?? lastPoint(growth)?.students ?? null
  const teachers = overview?.teachers.active ?? lastPoint(growth)?.teachers ?? null
  const parents = engagement?.parents.total ?? lastPoint(growth)?.parents ?? null
  const schoolAdmins =
    schools === null ? null : schools.reduce((n, s) => n + s.adminCount, 0)

  /* 🔴 ALL FOUR OR NONE — see `totalUsers`. */
  const parts = [students, teachers, parents, schoolAdmins]
  const totalUsers = parts.every((n) => n !== null)
    ? (parts as number[]).reduce((a, b) => a + b, 0)
    : null

  return {
    students,
    teachers,
    parents,
    schoolAdmins,
    totalUsers,
    activeStudents: overview?.engagement.activeStudents ?? null,
    activeWindowDays: overview?.engagement.windowDays ?? null,
    activeParents: engagement?.parents.active ?? null,
    activeTeachers: engagement?.teachers.active ?? null,
    engagementWindowDays: engagement?.windowDays.active ?? null,
    sampled: engagement?.truncated ?? false,
  }
}

/** The census nothing has loaded into yet — every figure unknown. */
export const emptyCensus = (): PlatformCensus => ({ ...EMPTY })

/**
 * The last day on the growth curve — the cumulative totals as of today.
 *
 * ⚠️ THE LAST POINT, NOT THE LARGEST. The series is cumulative over records that
 * survive today, so it is monotonic only while nobody is deleted; a `Math.max`
 * would quietly report a peak as if it were the present.
 */
function lastPoint(growth: GrowthSeries | null) {
  if (growth === null || growth.points.length === 0) return null
  return growth.points[growth.points.length - 1]
}

/**
 * A figure for a tile: the number, or the dash that means "not known here".
 *
 * 🔴 ONE HELPER SO THE DASH IS THE SAME CHARACTER EVERYWHERE. The screens used
 * a mix of `—` typed inline; a tile that renders `-` beside one that renders `—`
 * reads as two different states.
 */
export function tileValue(n: number | null): string {
  return n === null ? '—' : n.toLocaleString()
}

/**
 * `12 of 44` — a count with the denominator it is a share of.
 *
 * ⚠️ OD-39/OD-72: no count without its denominator, and never a bare percentage
 * where the pair is available. Returns the dash when either half is unknown.
 */
export function tilePair(n: number | null, of: number | null): string {
  if (n === null || of === null) return '—'
  return `${n.toLocaleString()} of ${of.toLocaleString()}`
}
